import React, { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { modalManager } from './manager'
import type { ModalContext } from './types'
import {
  getCurrentDoctorId,
  setProfessionalNpsClosedThisSession,
} from '../ui/ProfessionalNPS/services'
import professionalNPSAnalytics from '../services/analytics/ProfessionalNPS/professionalNPSAnalytics'

function pathMatchesRoute(pathname: string, pattern: string): boolean {
  if (pathname === pattern) return true
  return pathname.startsWith(pattern + '/')
}

function shouldShowModal(
  context: ModalContext,
  options: import('./types').ModalOpenOptions
): boolean {
  const { pathname } = context
  if (options.includeRoutes?.length) {
    const included = options.includeRoutes.some((p) =>
      pathMatchesRoute(pathname, p)
    )
    if (!included) return false
  }
  if (options.excludeRoutes?.length) {
    const excluded = options.excludeRoutes.some((p) =>
      pathMatchesRoute(pathname, p)
    )
    if (excluded) return false
  }
  if (options.shouldRender && !options.shouldRender(context)) {
    return false
  }
  return true
}

/**
 * Mount once in your app (e.g. App.tsx). Subscribes to the modal manager
 * and renders the current modal in a portal when include/exclude/shouldRender allow.
 * No provider – just a single host.
 */
export const ModalHost: React.FC<{
  container?: HTMLElement
  /** Optional: add extra context for shouldRender (e.g. user, feature flags). */
  getContext?: (base: ModalContext) => ModalContext
}> = ({ container, getContext }) => {
  const location = useLocation()
  const [entry, setEntry] = useState(modalManager.getCurrent())
  const portalTarget =
    container ?? (typeof document !== 'undefined' ? document.body : null)

  const context = useMemo<ModalContext>(() => {
    const base: ModalContext = {
      pathname: location.pathname,
      search: location.search,
    }
    return getContext ? getContext(base) : base
  }, [location.pathname, location.search, getContext])

  useEffect(() => {
    setEntry(modalManager.getCurrent())
    return modalManager.subscribe(() => {
      setEntry(modalManager.getCurrent())
    })
  }, [])

  if (!portalTarget) return null

  if (!entry) return null

  if (entry.options && !shouldShowModal(context, entry.options)) {
    return null
  }

  const { Component, props } = entry
  const componentName = Component.displayName ?? Component.name ?? 'AnonymousModal'
  const onClose = () => modalManager.close()
  const mergedProps = { ...props, onClose } as React.ComponentProps<
    typeof Component
  >

  const modalContent = React.createElement(Component, mergedProps)
  const overlay = (
    <div
      data-testid="modal-overlay"
      data-modal-component={componentName}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (componentName === 'ProfessionalNPS') {
            professionalNPSAnalytics.trackClose('unknown', 'outside_click')
            setProfessionalNpsClosedThisSession(getCurrentDoctorId())
          }
          onClose()
        }
      }}
      role="dialog"
      aria-modal
    >
      <div
        data-testid="modal-content"
        data-modal-component={componentName}
        style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {modalContent}
      </div>
    </div>
  )

  return createPortal(overlay, portalTarget)
}
